import { createContext, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useCartContext } from "@/features/cart/context/CartContext";
import type { CartResponse } from "@/features/cart/types/cart.type";
import { useDealsCartContext } from "@/features/deals/context/DealsCartContext";
import type { DealCart } from "@/features/deals/types/dealCart.type";
import { useMyRentalBookings } from "@/features/rentals/hooks/useMyRentalBookings";
import { useRentalCart } from "@/features/rentals/hooks/useRentalCart";
import type { RentalBooking } from "@/features/rentals/types/rentals.type";
import { appSettingsQueryOptions } from "@/shared/hooks/useAppSettings";
import { useCartSelector } from "@/shared/hooks/useCartSelector";
import type { CartType, UnifiedCart } from "@/shared/types/unified-cart.type";
import {
  transformBoutiqueCart,
  transformDealsCart,
  transformRentalsCart,
} from "@/shared/utils/cart-transformers";

type CartSelectionValue = {
  activeCartType: CartType
  setActiveCartType: (type: CartType) => void
};

type CartCountsValue = {
  boutiqueItemCount: number
  dealsItemCount: number
  rentalsItemCount: number
  rentalsSubtotal: number
};

type CartDataValue = {
  cart: UnifiedCart | undefined
  totalItems: number
  totalAmount: number
  isLoading: boolean
  isError: boolean
  originalBoutiqueCart: CartResponse | undefined
  originalDealsCart: DealCart | undefined
  /** Cart-status bookings only — kept exposed for callers that need
   * the original `RentalBooking` (e.g. to call `initiatePayment`). */
  rentalsCartBookings: RentalBooking[]
  /** Phase 1+ — `prefix_id` du cart unifié (créé à la volée par
   * l'API si absent). Utilisé par le CartPage pour issue checkout /
   * empty cart-level. `null` si le feature est masqué pour l'org. */
  rentalsCartId: string | null
};

type UnifiedCartContextValue =
  & CartSelectionValue
  & CartCountsValue
  & CartDataValue;

const CartSelectionContext = createContext<CartSelectionValue | undefined>(undefined);
const CartCountsContext = createContext<CartCountsValue | undefined>(undefined);
const CartDataContext = createContext<CartDataValue | undefined>(undefined);

export const UnifiedCartProvider = ({ children }: { children: React.ReactNode }) => {
  const [activeCartType, setActiveCartType] = useCartSelector();

  // Get both cart contexts
  const {
    cart: boutiqueCart,
    totalItems: boutiqueTotalItems,
    isLoading: isBoutiqueLoading,
    isError: isBoutiqueError,
  } = useCartContext();

  const {
    cart: dealsCart,
    totalItems: dealsTotalItems,
    isLoading: isDealsLoading,
    isError: isDealsError,
  } = useDealsCartContext();

  // Rentals: the "cart" is the subset of bookings still in `cart`
  // status. Each booking is paid individually, so we don't aggregate
  // into a UnifiedCart — we just count + sum subtotals for the tab
  // header, and let the consumer (CartPage) render the cards itself.
  //
  // Feature flag `service_catalog` : ce Provider vit AU-DESSUS du
  // `<Router>` (App.tsx) donc on ne peut PAS utiliser `useIsFeatureHidden`
  // (qui dépend de `useLocation`). On réutilise les options partagées
  // `appSettingsQueryOptions` (clé stable + staleTime long) pour partager le
  // cache avec `useAppSettings` => un seul appel `/app-settings` par session.
  // On désactive `useMyRentalBookings` quand `service_catalog` est masqué.
  const { data: appSettings, isPending: isSettingsPending, isError: isSettingsError } = useQuery(appSettingsQueryOptions());
  const isRentalsHidden = appSettings?.hidden_features?.includes("service_catalog") ?? false;
  const {
    data: rentalsBookingsData,
    isPending: isBookingsPending,
    isError: isBookingsError,
  } = useMyRentalBookings({
    // On attend que `appSettings` soit chargé pour éviter de faire
    // partir une 1ère requête `/catalog/bookings` avant de savoir si
    // le feature est désactivé.
    enabled: appSettings !== undefined && !isRentalsHidden,
  });

  // Phase 1+ — Cart entity côté API : handle des mutations cart-level
  // (checkout, empty, subsidy), et périmètre du panier affiché.
  const {
    data: rentalsCartData,
    isPending: isCartPending,
    isError: isCartError,
  } = useRentalCart({ enabled: appSettings !== undefined && !isRentalsHidden });

  // Both responses are needed to distinguish an empty cart from an unknown one.
  // isPending also covers the initial wait for settings; cached background refreshes
  // keep displaying the existing cart. Errors must not become an endless spinner.
  const isRentalsError = isSettingsError || (!isRentalsHidden && (isBookingsError || isCartError));
  const isRentalsLoading = !isRentalsError
    && (isSettingsPending || (!isRentalsHidden && (isBookingsPending || isCartPending)));

  const rentalsCartBookings = useMemo(() => {
    const pending = (rentalsBookingsData?.bookings ?? []).filter((b) => b.status === "open");
    // Le panier du salarié, pas « tout ce qui est en pending_checkout » :
    // un brouillon posé par un élu vit dans un panier `admin`, lui aussi en
    // pending_checkout, et s'affichait ici en gonflant le sous-total alors
    // que le checkout ne le facturait pas (ANO-25). Tant que le cart n'est
    // pas chargé, on n'affiche rien plutôt qu'un panier possiblement faux.
    const cartIds = rentalsCartData?.cart?.bookings.map((cartBooking) => cartBooking.id);
    if (!cartIds) return [];
    return pending.filter((b) => cartIds.includes(b.id));
  }, [rentalsBookingsData, rentalsCartData]);
  const rentalsItemCount = rentalsCartBookings.length;
  const rentalsSubtotal = useMemo(
    () => rentalsCartBookings.reduce((sum, b) => sum + (b.total_price_cents ?? 0) / 100, 0),
    [rentalsCartBookings],
  );

  // La liste affichée reste alimentée par `useMyRentalBookings` pour ne
  // pas dupliquer la shape de données (le transformer attend RentalBooking,
  // pas la projection summary du cart) ; le cart borne le périmètre.
  const rentalsCartId = rentalsCartData?.cart?.id ?? null;

  // Transform carts to unified format
  const unifiedBoutiqueCart = useMemo(
    () => transformBoutiqueCart(boutiqueCart),
    [boutiqueCart],
  );

  const unifiedDealsCart = useMemo(
    () => transformDealsCart(dealsCart),
    [dealsCart],
  );

  const unifiedRentalsCart = useMemo(
    () => transformRentalsCart(rentalsCartBookings),
    [rentalsCartBookings],
  );

  // Select the active cart based on type.
  const cart = useMemo(() => {
    if (activeCartType === "boutique") return unifiedBoutiqueCart;
    if (activeCartType === "deals") return unifiedDealsCart;
    return unifiedRentalsCart;
  }, [activeCartType, unifiedBoutiqueCart, unifiedDealsCart, unifiedRentalsCart]);

  const isLoading = activeCartType === "boutique"
    ? isBoutiqueLoading
    : activeCartType === "deals"
      ? isDealsLoading
      : isRentalsLoading;
  const isError = activeCartType === "boutique"
    ? isBoutiqueError
    : activeCartType === "deals"
      ? isDealsError
      : isRentalsError;

  const totalItems = cart?.total_items ?? 0;
  const totalAmount = cart?.total_amount ?? 0;

  const selectionValue = useMemo<CartSelectionValue>(
    () => ({ activeCartType, setActiveCartType }),
    [activeCartType, setActiveCartType],
  );

  const countsValue = useMemo<CartCountsValue>(
    () => ({
      boutiqueItemCount: boutiqueTotalItems,
      dealsItemCount: dealsTotalItems,
      rentalsItemCount,
      rentalsSubtotal,
    }),
    [boutiqueTotalItems, dealsTotalItems, rentalsItemCount, rentalsSubtotal],
  );

  const dataValue = useMemo<CartDataValue>(
    () => ({
      cart,
      totalItems,
      totalAmount,
      isLoading,
      isError,
      originalBoutiqueCart: boutiqueCart,
      originalDealsCart: dealsCart,
      rentalsCartBookings,
      rentalsCartId,
    }),
    [
      cart,
      totalItems,
      totalAmount,
      isLoading,
      isError,
      boutiqueCart,
      dealsCart,
      rentalsCartBookings,
      rentalsCartId,
    ],
  );

  return (
    <CartSelectionContext.Provider value={selectionValue}>
      <CartCountsContext.Provider value={countsValue}>
        <CartDataContext.Provider value={dataValue}>
          {children}
        </CartDataContext.Provider>
      </CartCountsContext.Provider>
    </CartSelectionContext.Provider>
  );
};

export const useCartCounts = (): CartCountsValue => {
  const context = useContext(CartCountsContext);
  if (!context) {
    throw new Error("useCartCounts must be used within a UnifiedCartProvider");
  }
  return context;
};

export const useCartSelection = (): CartSelectionValue => {
  const context = useContext(CartSelectionContext);
  if (!context) {
    throw new Error("useCartSelection must be used within a UnifiedCartProvider");
  }
  return context;
};

export const useCartData = (): CartDataValue => {
  const context = useContext(CartDataContext);
  if (!context) {
    throw new Error("useCartData must be used within a UnifiedCartProvider");
  }
  return context;
};

export const useUnifiedCartContext = (): UnifiedCartContextValue => {
  const selection = useCartSelection();
  const counts = useCartCounts();
  const data = useCartData();
  return { ...selection, ...counts, ...data };
};
